
import React, { useState, useMemo } from 'react';
import { dbService } from '../services/dbService.ts';
import { aiService } from '../services/aiService.ts';
import { ChatRequest, UserRole, SubscriptionTier, RequestStatus } from '../types.ts';

const AutomationView = ({ user, onChangeTab }: any) => {
  const [requests, setRequests] = useState<ChatRequest[]>(dbService.getRequestsForUser(user.id, UserRole.STUDENT));
  const [isDrafting, setIsDrafting] = useState<string | null>(null);

  const activeConnections = useMemo(() => 
    requests.filter(r => r.status === RequestStatus.ACCEPTED || r.status === RequestStatus.COMPLETED),
    [requests]
  );

  const userTier = user.tier || SubscriptionTier.BASIC;
  const isLocked = userTier === SubscriptionTier.BASIC;

  const handleToggleAutomation = async (req: ChatRequest) => {
    const isEnabled = !req.followUpEnabled;
    const updated = { ...req, followUpEnabled: isEnabled, updatedAt: Date.now() };
    
    if (isEnabled && !req.followUpDraft) {
      setIsDrafting(req.id);
      try {
        const studentProfile = dbService.getStudentProfile(user.id);
        const profProfile = dbService.getProfessionalProfile(req.professionalId);
        const draft = await aiService.generatePostCallFollowUp(
          studentProfile, 
          profProfile, 
          req.studentNote || "Previous conversation on career growth."
        );
        updated.followUpDraft = draft.followUpDraft;
        updated.followUpFrequency = '2weeks'; // Default
        updated.lastFollowUpAt = Date.now();
      } catch (e) { console.error(e); }
      finally { setIsDrafting(null); }
    }

    dbService.saveRequest(updated);
    setRequests(dbService.getRequestsForUser(user.id, UserRole.STUDENT));
  };

  const handleSetFrequency = (reqId: string, freq: '1week' | '2weeks' | '1month') => {
    const req = requests.find(r => r.id === reqId);
    if (req) {
      const updated = { ...req, followUpFrequency: freq, updatedAt: Date.now() };
      dbService.saveRequest(updated);
      setRequests(dbService.getRequestsForUser(user.id, UserRole.STUDENT));
    }
  };

  const calculateNextSync = (lastAt: number | undefined, freq: string | undefined) => {
    if (!lastAt || !freq) return "Protocol Idle";
    const now = Date.now();
    let ms = 0;
    if (freq === '1week') ms = 7 * 86400000;
    else if (freq === '2weeks') ms = 14 * 86400000;
    else ms = 30 * 86400000;
    
    const target = lastAt + ms;
    const diff = target - now;
    if (diff <= 0) return "Triggering Now";
    const days = Math.ceil(diff / 86400000);
    return `Next Sync in ${days}d`;
  };

  return (
    <div className="p-12 lg:p-20 bg-slate-50/30 dark:bg-slate-950/30 min-h-screen transition-colors duration-500 overflow-y-auto font-sans">
        <header className="mb-16">
          <span className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-600 dark:text-indigo-400 mb-3 block">Relational Maintenance</span>
          <h1 className="text-6xl font-black text-blue-950 dark:text-white tracking-tighter">Persistence.</h1>
          <p className="mt-4 text-slate-400 dark:text-slate-500 font-medium max-w-xl">Automated handshake cycles to ensure your network nodes remain high-fidelity and active.</p>
        </header>

        {isLocked ? (
          <div className="bg-white dark:bg-slate-900 p-20 rounded-[4rem] border border-slate-100 dark:border-slate-800 shadow-4xl text-center flex flex-col items-center animate-in zoom-in-95 duration-500">
             <div className="w-24 h-24 bg-blue-50 dark:bg-indigo-900/20 text-blue-600 dark:text-indigo-400 rounded-[2.5rem] flex items-center justify-center text-4xl mb-10 shadow-inner">
                <i className="fas fa-lock"></i>
             </div>
             <h2 className="text-4xl font-black text-blue-950 dark:text-white tracking-tighter mb-4">Persistence Engine Locked.</h2>
             <p className="text-slate-500 dark:text-slate-400 text-lg font-medium max-w-md mb-12 leading-relaxed">
               Relational Automation is a high-velocity feature for <span className="text-blue-600 dark:text-indigo-400 font-black">Standard</span> and <span className="text-blue-600 dark:text-indigo-400 font-black">Advanced</span> tiers.
             </p>
             <button 
               onClick={() => onChangeTab('membership')}
               className="px-12 py-5 bg-blue-900 dark:bg-indigo-600 text-white rounded-3xl font-black text-lg hover:bg-blue-800 transition shadow-2xl shadow-blue-900/20 active:scale-95"
             >
                Upgrade Plan
             </button>
          </div>
        ) : (
          <div className="space-y-10 max-w-6xl">
             {activeConnections.length === 0 ? (
               <div className="bg-white dark:bg-slate-900 p-20 rounded-[4rem] border-2 border-dashed border-slate-100 dark:border-slate-800 text-center opacity-30">
                  <i className="fas fa-link-slash text-6xl mb-6 text-slate-300"></i>
                  <p className="font-black uppercase tracking-widest text-sm">No active connection nodes found.</p>
               </div>
             ) : activeConnections.map(req => {
               const prof = dbService.getProfessionalProfile(req.professionalId);
               return (
                 <div key={req.id} className="bg-white dark:bg-slate-900 p-10 rounded-[3.5rem] border border-slate-100 dark:border-slate-800 shadow-2xl flex flex-col lg:flex-row gap-12 group transition-all hover:border-indigo-500/20">
                    <div className="flex flex-col items-center lg:items-start shrink-0">
                       <div className="relative mb-6">
                          <div className="w-24 h-24 bg-blue-50 dark:bg-indigo-900/20 text-blue-600 dark:text-indigo-400 rounded-3xl flex items-center justify-center text-4xl font-black shadow-inner border border-blue-100 dark:border-indigo-900/50">
                             {prof?.fullName[0]}
                          </div>
                          {req.followUpEnabled && (
                             <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full border-4 border-white dark:border-slate-950 flex items-center justify-center text-[10px] text-white animate-pulse">
                                <i className="fas fa-sync-alt"></i>
                             </div>
                          )}
                       </div>
                       <h3 className="text-xl font-black text-blue-950 dark:text-white text-center lg:text-left">{prof?.fullName}</h3>
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{prof?.company}</p>
                    </div>

                    <div className="flex-1 space-y-8">
                       <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                          <div>
                             <h4 className="text-sm font-black text-blue-950 dark:text-white uppercase tracking-widest mb-2">Protocol Persistence</h4>
                             <p className="text-xs text-slate-400 dark:text-slate-500 font-bold">Automated follow-up cycles to stay top-of-mind.</p>
                          </div>
                          <button 
                            onClick={() => handleToggleAutomation(req)}
                            disabled={isDrafting === req.id}
                            className={`px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                              req.followUpEnabled 
                              ? 'bg-emerald-500 text-white shadow-lg' 
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 hover:bg-indigo-600 hover:text-white'
                            }`}
                          >
                             {isDrafting === req.id ? <i className="fas fa-circle-notch animate-spin mr-2"></i> : null}
                             {req.followUpEnabled ? 'Protocol Active' : 'Initiate Cycle'}
                          </button>
                       </div>

                       {req.followUpEnabled && (
                         <div className="bg-slate-50 dark:bg-slate-800/50 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 space-y-8 animate-in slide-in-from-top-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                               <div className="space-y-4">
                                  <p className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Frequency Selection</p>
                                  <div className="flex gap-2 p-1 bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800">
                                     {['1week', '2weeks', '1month'].map(f => (
                                       <button 
                                         key={f}
                                         onClick={() => handleSetFrequency(req.id, f as any)}
                                         className={`flex-1 py-3 rounded-lg text-[9px] font-black uppercase transition-all ${req.followUpFrequency === f ? 'bg-blue-950 text-white shadow-md' : 'text-slate-400 hover:text-blue-950 dark:hover:text-white'}`}
                                       >
                                         {f.replace('week', ' Wk').replace('month', ' Mo')}
                                       </button>
                                     ))}
                                  </div>
                               </div>
                               <div className="space-y-4">
                                  <p className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Sync Status</p>
                                  <div className="px-6 py-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                     <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{calculateNextSync(req.lastFollowUpAt, req.followUpFrequency)}</span>
                                     <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                                  </div>
                               </div>
                            </div>

                            <div className="space-y-4">
                               <div className="flex justify-between items-center">
                                  <p className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Sage AI Handshake Draft</p>
                                  <button className="text-[10px] font-black text-slate-400 hover:text-blue-600 uppercase tracking-widest">Regenerate</button>
                               </div>
                               <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 text-sm font-bold text-slate-500 dark:text-slate-400 leading-relaxed italic">
                                  "{req.followUpDraft || 'Drafting relational anchor...'}"
                                </div>
                            </div>
                         </div>
                       )}
                    </div>
                 </div>
               );
             })}
          </div>
        )}
    </div>
  );
};

export default AutomationView;
